import { Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BreadcrumbParams } from '@angular-monorepo/ui';

export interface NavigationState {
  workflowId?: number;
  workflowName?: string;
  executionId?: number;
  executionName?: string;
  currentRoute?: string;
  breadcrumbPath?: BreadcrumbItem[];
}

export interface BreadcrumbItem {
  label?: string;
  routerLink?: string;
  icon?: string;
  disabled?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationStoreService {
  private navigationState = signal<NavigationState>({});
  private navigationSubject = new BehaviorSubject<NavigationState>({});

  public navigation$ = this.navigationSubject.asObservable();

  getCurrentState(): NavigationState {
    return this.navigationState();
  }

  updateNavigationState(state: Partial<NavigationState>): void {
    const currentState = this.navigationState();
    const newState = { ...currentState, ...state };
    this.navigationState.set(newState);
    this.navigationSubject.next(newState);
  }

  setWorkflowInfo(workflowId: number, workflowName: string): void {
    this.updateNavigationState({
      workflowId,
      workflowName,
      currentRoute: 'workflow-executions',
      breadcrumbPath: [
        { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
        { label: 'Monitoreo', routerLink: '/monitoreo' },
        { label: workflowName, routerLink: '/monitoreo' },
        { label: 'Ejecuciones', disabled: true },
      ],
    });
  }

  setExecutionInfo(executionId: number, executionName: string): void {
    const currentState = this.navigationState();
    this.updateNavigationState({
      executionId,
      executionName,
      currentRoute: 'execution-history',
      breadcrumbPath: [
        { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
        { label: 'Monitoreo', routerLink: '/monitoreo' },
        {
          label: currentState.workflowName || 'Workflow',
          routerLink: `/monitoreo/workflow/${currentState.workflowId}`,
        },
        { label: executionName, routerLink: `/monitoreo/workflow/${currentState.workflowId}` },
        { label: `Historial Id: ${executionId}`, disabled: true },
      ],
    });
  }

  setMonitoringBreadcrumbs(): void {
    this.updateNavigationState({
      currentRoute: 'monitoring',
      breadcrumbPath: [
        { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
        { label: 'Monitoreo', disabled: true },
      ],
    });
  }

  clearNavigationState(): void {
    this.navigationState.set({});
    this.navigationSubject.next({});
  }

  getCurrentBreadcrumbs(): BreadcrumbItem[] {
    const state = this.navigationState();
    return state.breadcrumbPath || [];
  }

  generateBreadcrumbs(route: string, params?: BreadcrumbParams): BreadcrumbItem[] {
    const state = this.navigationState();

    switch (route) {
      case 'workflow-executions':
        return [
          { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
          { label: 'Monitoreo', routerLink: '/monitoreo' },
          { label: state.workflowName || 'Workflow', routerLink: '/monitoreo', disabled: true },
          { label: 'Ejecuciones', disabled: true },
        ];

      case 'execution-history': {
        const executionId = params?.executionId || state.executionId;
        const executionName = params?.executionName || state.executionName;
        const workflowId = params?.workflowId || state.workflowId;
        const workflowName = params?.workflowName || state.workflowName;

        return [
          { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
          { label: 'Monitoreo', routerLink: '/monitoreo' },
          {
            label: workflowName || `Workflow ${workflowId}`,
            routerLink: `/monitoreo/workflow/${workflowId}`,
          },
          {
            label: executionName || `Ejecución ${executionId}`,
            routerLink: `/monitoreo/workflow/${workflowId}`,
            disabled: true,
          },
          {
            label: `Historial Id: ${executionId}`,
            disabled: true,
          },
        ];
      }

      default:
        return this.getDefaultBreadcrumbs();
    }
  }

  private getDefaultBreadcrumbs(): BreadcrumbItem[] {
    return [
      { label: 'Dashboard', routerLink: '/dashboard', icon: 'pi pi-home' },
      { label: 'Monitoreo', disabled: true },
    ];
  }
}
